import React from 'react';
import { cn } from '../utils/cn';

export type CardVariant = 'elevated' | 'filled' | 'outlined' | 'tonal';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  interactive?: boolean;
  /** Legacy prop – use children w/ CardHeader instead. */
  title?: string;
  /** Legacy prop – use children w/ CardFooter instead. */
  footer?: React.ReactNode;
}

const variantStyles: Record<CardVariant, string> = {
  elevated: 'bg-surface text-surface-foreground shadow-level-1 border border-outline/30',
  filled: 'bg-primary-container text-primary-container-foreground',
  outlined: 'bg-transparent border border-outline text-surface-foreground',
  tonal: 'bg-surface-variant text-surface-variant-foreground',
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ children, variant = 'elevated', interactive = false, title, footer, className, ...props }, ref) => {
    if (title || footer) {
      // Legacy compatibility
      return (
        <div
          ref={ref}
          className={cn('rounded-md', variantStyles[variant], className)}
          {...props}
        >
          {title ? (
            <div className="px-5 py-4 border-b border-outline/30">
              <h3 className="text-title-md font-semibold">{title}</h3>
            </div>
          ) : null}
          <div className="px-5 py-4">{children}</div>
          {footer ? <div className="px-5 py-4 border-t border-outline/30">{footer}</div> : null}
        </div>
      );
    }
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-md',
          variantStyles[variant],
          interactive &&
            'cursor-pointer transition-all duration-2 ease-standard hover:shadow-level-2 active:scale-[0.99] focus-visible:outline-none focus-visible:shadow-focus-ring',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Card.displayName = 'Card';

export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn('flex items-start justify-between gap-3 p-5 pb-3', className)} {...props} />
);

export const CardTitle: React.FC<React.HTMLAttributes<HTMLHeadingElement> & { as?: React.ElementType }> = ({
  as: Tag = 'h3',
  className,
  ...props
}) => <Tag className={cn('text-title-md font-semibold text-surface-foreground', className)} {...props} />;

export const CardDescription: React.FC<React.HTMLAttributes<HTMLParagraphElement>> = ({ className, ...props }) => (
  <p className={cn('text-body-md text-muted-foreground', className)} {...props} />
);

export const CardContent: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn('px-5 pb-5 pt-0', className)} {...props} />
);

export const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn('flex items-center justify-end gap-2 px-5 pb-5 pt-0', className)} {...props} />
);
